import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getCustomerById } from "~/lib/api/api-client";
import { Container, Box, Paper, Alert, AlertTitle, Button, Typography, CircularProgress } from "@mui/material";
import { ArrowBack, Phone, QrCode } from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

interface CustomerData {
  id: number;
  barcode: string;
  name: string;
  phone: string;
  pointsBalance: number;
  createdAt: string;
  updatedAt: string;
}

interface Transaction {
  id: number;
  type: string;
  amountEGP: number | null;
  pointsDelta: number;
  createdAt: string;
  notes: string | null;
  cashier_name?: string;
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const viewOnly = searchParams.get("viewOnly") === "true";

  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        const result = await getCustomerById(id);
        if (result.success) {
          setCustomer(result.customer);
          setRecentTransactions(result.recentTransactions || []);
        } else {
          setError(result.error || "Failed to fetch customer");
        }
      } catch (err: any) {
        setError(err.message || "Failed to fetch customer");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const transactionTypeLabels: Record<string, string> = {
    EARN: "شراء",
    REDEEM_DIRECT: "استبدال مباشر",
    REDEEM_RETURN: "استبدال إرجاع",
  };
  // Suppress unused warning
  void transactionTypeLabels;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  if (error || !customer) {
    return (
      <Container maxWidth="xl" sx={{ p: 2 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>خطأ</AlertTitle>
            {error || "العميل غير موجود"}
          </Alert>
          <Button variant="contained" onClick={() => navigate("/")}>عودة للرئيسية</Button>
        </Paper>
      </Container>
    );
  }

  const columns: GridColDef[] = [
    { field: "cashier_name", headerName: "الكاشير", flex: 1 },
    {
      field: "type",
      headerName: "النوع",
      flex: 1,
      renderCell: (params) => (
        params.value === "EARN" ? (
          <Box sx={{ backgroundColor: "#e8f5e9", color: "#1b5e20", px: 1, py: 0.5, borderRadius: 1 }}>إضافة</Box>
        ) : (
          <Box sx={{ backgroundColor: "#ffebee", color: "#b71c1c", px: 1, py: 0.5, borderRadius: 1 }}>خصم</Box>
        )
      ),
    },
    {
      field: "amount",
      headerName: "المبلغ",
      flex: 1,
      valueGetter: (_value, row) => {
        const tx = row as Transaction;
        if (tx.amountEGP) return `${tx.amountEGP} ج.م`;
        if (tx.pointsDelta) return `${Math.abs(tx.pointsDelta)} نقطة`;
        return "-";
      },
    },
  ];

  const rows = recentTransactions.map((tx) => ({
    id: tx.id,
    cashier_name: tx.cashier_name || "-",
    type: tx.type,
    amount: tx.amountEGP || tx.pointsDelta || null,
  }));

  return (
    <Container maxWidth="xl" sx={{ p: 2 }}>
      {/* Back button */}
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} variant="text" onClick={() => navigate("/")}>
          رجوع للبحث
        </Button>
      </Box>

      {/* Customer Info Card */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box component="img" src="/placeholder-avatar.png" alt="avatar" sx={{ width: 56, height: 56, borderRadius: "50%" }} />
            <Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>{customer.name}</Typography>
              <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Phone fontSize="small" sx={{ color: "gray" }} />
                  <Typography variant="body2" sx={{ color: "gray" }}>{customer.phone}</Typography>
                </Box>
                {!viewOnly && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <QrCode fontSize="small" sx={{ color: "gray" }} />
                    <Typography variant="body2" sx={{ color: "gray" }}>{customer.barcode}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ backgroundColor: "#e8f5e9", p: 2, borderRadius: 2, textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: "gray" }}>رصيد النقاط</Typography>
            <Typography variant="h4" sx={{ color: "#1b5e20", fontWeight: "bold" }}>{customer.pointsBalance.toFixed(2)}</Typography>
          </Box>
        </Box>
        {viewOnly && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>وضع العرض فقط</AlertTitle>
            يرجى مسح الباركود للقيام بعمليات
          </Alert>
        )}
      </Paper>

      {/* Recent Transactions */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>آخر المعاملات</Typography>
        {recentTransactions.length > 0 ? (
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            hideFooterPagination
            hideFooterSelectedRowCount
            disableColumnMenu
          />
        ) : (
          <Typography variant="body2" color="text.secondary">لا توجد معاملات سابقة</Typography>
        )}
      </Paper>
    </Container>
  );
}
